import { useEffect, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { Pause, Play, RotateCcw, SkipForward } from 'lucide-react'
import type { Film as FilmBlok } from '../lib/types'
import Shots from './Shots'

/* ===========================================================================
 *  De filmspeler
 *
 *  Waarom hij niet vanzelf begint
 *  ------------------------------
 *
 *  Drie redenen, en ze wegen alle drie.
 *
 *    - Dit wordt op een tablet in een wasstraat bekeken, en soms op een
 *      telefoon met een bundel. Beweging die begint zonder dat je erom vroeg,
 *      is beweging die je niet had willen betalen.
 *    - Een browser blokkeert automatisch starten toch al de helft van de tijd,
 *      en dan zit je met een speler waarvan de helft van de mensen denkt dat
 *      hij stuk is.
 *    - Wie moeite heeft met bewegend beeld, hoort zelf te bepalen wanneer het
 *      begint. De tekst eronder vertelt hetzelfde verhaal; wie de film
 *      overslaat mist niets.
 *
 *  Waarom de ondertiteling geen ondertiteling is
 *  ---------------------------------------------
 *
 *  Er is geen geluid. De tekst onder het beeld IS de film -- hij staat er niet
 *  bij voor wie niets hoort, hij staat er voor iedereen. Dat scheelt een
 *  opnamestudio, het werkt in een lawaaiige hal, en het is met één regel te
 *  corrigeren als er iets fout in staat.
 *
 *  Waarom de scènes aanklikbaar zijn
 *  ---------------------------------
 *
 *  Omdat mensen terug willen. Een film van veertig seconden waarin je de kern
 *  mist en alleen helemaal opnieuw kunt beginnen, wordt één keer bekeken.
 * =========================================================================== */

export default function Film({
  blok, opKlaar,
}: {
  blok: FilmBlok
  /** Wordt aangeroepen als de film is uitgekeken. */
  opKlaar: () => void
}) {
  const [gestart, setGestart] = useState(false)
  const [bezig, setBezig] = useState(false)
  const [bij, setBij] = useState(0)
  const [ronde, setRonde] = useState(0)
  const [af, setAf] = useState(false)

  /* Hoeveel er van deze scène nog te gaan is. Nodig om na een pauze verder te
     kunnen in plaats van de scène opnieuw te beginnen. */
  const rest = useRef<number>(0)
  const begon = useRef<number>(0)

  const scene = blok.scenes[bij]
  const totaal = blok.scenes.reduce((s, x) => s + x.duur, 0)
  const tot = blok.scenes.slice(0, bij).reduce((s, x) => s + x.duur, 0)

  /* --- de klok --- */
  useEffect(() => {
    if (!bezig || !scene) return

    const duur = rest.current > 0 ? rest.current : scene.duur
    begon.current = Date.now()

    const t = window.setTimeout(() => {
      rest.current = 0
      if (bij + 1 < blok.scenes.length) {
        setBij(bij + 1)
      } else {
        setBezig(false)
        setAf(true)
        opKlaar()
      }
    }, duur)

    return () => {
      window.clearTimeout(t)
      /* Wat er nog over was, voor als dit een pauze was. Bij een scènewissel
         wordt hij hieronder toch op nul gezet. */
      const verstreken = Date.now() - begon.current
      rest.current = Math.max(0, duur - verstreken)
    }
  }, [bezig, bij, ronde, scene, blok.scenes.length, opKlaar])

  function speel() {
    if (!gestart) setGestart(true)
    if (af) { herbegin(); return }
    setBezig(true)
  }

  function pauzeer() { setBezig(false) }

  function herbegin() {
    rest.current = 0
    setAf(false)
    setBij(0)
    setRonde((r) => r + 1)
    setGestart(true)
    setBezig(true)
  }

  function naar(i: number) {
    rest.current = 0
    setAf(false)
    setBij(i)
    setRonde((r) => r + 1)
    setGestart(true)
    setBezig(true)
  }

  function verder() {
    if (bij + 1 < blok.scenes.length) naar(bij + 1)
    else { setBezig(false); setAf(true); opKlaar() }
  }

  /* --- een echte opname, als die er ooit komt --- */
  if (blok.bron) {
    return (
      <div className="film">
        <Kop blok={blok} />
        <div className="film-doek">
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video src={blok.bron} controls playsInline preload="metadata" style={{ width: '100%', height: '100%' }} />
        </div>
        <div className="film-zeg">
          {blok.scenes.map((s) => s.zeg).join(' ')}
        </div>
      </div>
    )
  }

  return (
    <div className="film">
      <Kop blok={blok} />

      <div className="film-doek">
        {/*
          * De sleutel is scène + ronde. Daarmee hangt React het element
          * opnieuw op bij elke wissel én bij een herstart, en beginnen de
          * CSS-animaties vanaf het begin. Zonder de ronde erin zou de eerste
          * scène bij "opnieuw" stil blijven staan op zijn eindbeeld.
          */}
        {gestart && scene && <Shots key={`${bij}-${ronde}`} shot={scene.shot} />}

        {!gestart && blok.scenes[0] && (
          <>
            {/* De still: dezelfde tekening, stilgezet op nul. Zie film.css. */}
            <div className="film-poster">
              <Shots shot={blok.scenes[0].shot} />
            </div>
            <button className="film-start" onClick={speel}>
              <span className="rondje"><Play size={32} fill="currentColor" /></span>
              <span className="woord">{Math.round(totaal / 1000)} seconden</span>
            </button>
          </>
        )}
      </div>

      {/* De tekst. aria-live, zodat een schermlezer hem bij elke scène
          voorleest in plaats van alleen de eerste. */}
      <p className="film-zeg" aria-live="polite">
        {gestart ? (scene?.zeg ?? '') : blok.ondertitel}
      </p>

      <div className="film-voet">
        <button
          className="knop"
          onClick={af ? herbegin : bezig ? pauzeer : speel}
          aria-label={af ? 'Opnieuw' : bezig ? 'Pauze' : 'Afspelen'}
        >
          {af ? <RotateCcw size={16} /> : bezig ? <Pause size={16} /> : <Play size={16} />}
          {af ? 'Opnieuw' : bezig ? 'Pauze' : gestart ? 'Verder' : 'Afspelen'}
        </button>

        <div className="film-rail">
          {blok.scenes.map((s, i) => (
            <button
              key={i}
              type="button"
              className={`film-segment ${i < bij || af ? 'gehad' : ''} ${i === bij && !af ? 'nu' : ''} ${!bezig ? 'pauze' : ''}`}
              style={{ '--scene-duur': `${s.duur}ms` } as CSSProperties}
              onClick={() => naar(i)}
              aria-label={`Scène ${i + 1}: ${s.zeg}`}
              title={s.zeg}
            >
              <i />
            </button>
          ))}
        </div>

        <span className="film-tijd">
          {tel(af ? totaal : tot)} / {tel(totaal)}
        </span>

        {!af && (
          <button className="knop kaal" onClick={verder} aria-label="Volgende scène">
            <SkipForward size={16} />
          </button>
        )}
      </div>
    </div>
  )
}

function Kop({ blok }: { blok: FilmBlok }) {
  return (
    <div className="film-kop">
      <span className="merkje">Film</span>
      <h4>{blok.titel}</h4>
    </div>
  )
}

function tel(ms: number): string {
  const s = Math.round(ms / 1000)
  return `0:${String(s).padStart(2, '0')}`
}
